// socket.service.ts
import { Injectable, inject } from "@angular/core";
import { io, Socket } from "socket.io-client";
import { environment } from "../../../../environments/environment";
import { Observable, Subject, firstValueFrom } from "rxjs";
import { Event } from "../interfaces/event.interface";
import { DatadogService } from "../../observability/datadog.service";

@Injectable({
  providedIn: "root",
})
export class SocketService {
  private socket: Socket;
  private connectedSubject = new Subject<void>();
  private disconnectedSubject = new Subject<string>();
  private wsFailedOnce = false;
  private datadogService = inject(DatadogService);

  /**
   * Observable que emite quando socket conecta
   */
  public readonly onConnected$ = this.connectedSubject.asObservable();

  /**
   * Observable que emite quando socket desconecta (com razão)
   */
  public readonly onDisconnected$ = this.disconnectedSubject.asObservable();

  constructor() {
    this.socket = io(environment.socketUrl, {
      autoConnect: false, // Não conectar automaticamente
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000, // 10s timeout para conexão inicial
      withCredentials: true, // Enviar credenciais para CORS funcionar
      // Preferir websocket, mas permitir fallback para polling se handshake WS falhar
      transports: ["websocket", "polling"],
    });
    this.setupEventListeners();
  }

  /**
   * Configurar listeners de eventos do Socket.io
   */
  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this.datadogService.logInfo('[Socket] Connected successfully', {
        socketId: this.socket.id,
        transport: (this.socket.io as any).engine?.transport?.name,
      });
      this.connectedSubject.next();
    });

    this.socket.on('disconnect', (reason) => {
      this.datadogService.logWarn('[Socket] Disconnected', {
        reason,
        socketId: this.socket.id,
      });
      this.disconnectedSubject.next(reason);
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      this.datadogService.logInfo('[Socket] Reconnected after attempts', {
        attemptNumber,
        socketId: this.socket.id,
        transport: (this.socket.io as any).engine?.transport?.name,
      });
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      this.datadogService.logInfo('[Socket] Reconnection attempt', {
        attemptNumber,
      });
    });

    this.socket.on('reconnect_error', (error: Error) => {
      this.datadogService.logError('[Socket] Reconnection error', {
        error: String(error),
        attemptNumber: (this.socket.io as any)._reconnectionAttempts,
      });
    });

    this.socket.on('reconnect_failed', () => {
      this.datadogService.logError('[Socket] Reconnection failed after all attempts', {
        maxAttempts: (this.socket.io as any).opts.reconnectionAttempts,
      });
    });

    this.socket.on('connect_error', (error) => {
      this.datadogService.logError('[Socket] Connection error', {
        error: String(error),
        socketId: this.socket.id,
        connected: this.socket.connected,
        readyState: (this.socket.io as any).engine?.readyState,
        transport: (this.socket.io as any).engine?.transport?.name,
      });
      console.error('Socket.io connection error:', error);
      // Log mais detalhado do estado do socket
      console.error('[Socket][debug]', {
        id: this.socket.id,
        connected: this.socket.connected,
        // @ts-ignore propriedades internas do engine
        readyState: this.socket.io.engine?.readyState,
        // @ts-ignore transporte atual
        transport: this.socket.io.engine?.transport?.name,
        opts: this.socket.io.opts,
      });
      if (!this.wsFailedOnce) {
        this.wsFailedOnce = true;
        // força fallback para polling se o handshake WS falhar
        // @ts-ignore acessar opts internos
        this.socket.io.opts.transports = ["polling"];
        this.datadogService.logInfo('[Socket] Forcing fallback to polling after WS error');
        console.warn("[Socket] Forçando fallback para polling após erro de WS");
        this.connect();
      }
    });
  }

  connect() {
    if (this.socket.connected) {
      this.datadogService.logInfo('[Socket] Connect called but already connected', {
        socketId: this.socket.id,
      });
      return;
    }
    this.datadogService.logInfo('[Socket] Initiating connection');
    console.info("[Socket] connect() chamado");
    this.socket.connect();
  }

  disconnect() {
    this.datadogService.logInfo('[Socket] Disconnecting', {
      socketId: this.socket.id,
      wasConnected: this.socket.connected,
    });
    this.socket.disconnect();
  }

  /**
   * Verifica se o socket está conectado
   */
  isConnected(): boolean {
    return this.socket.connected;
  }

  /**
   * Obtém o ID do socket (se conectado)
   */
  getSocketId(): string | undefined {
    return this.socket.id;
  }

  onEvent<T = Event>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      const listener = (data: T) => {
        observer.next(data);
      };
      this.socket.on(event, listener);
      return () => {
        this.socket.off(event, listener);
      };
    });
  }

  emitWithAck<T = any, R = any>(event: string, data?: T): Observable<R> {
    return new Observable<R>((observer) => {
      const doEmit = () => {
        this.socket.emit(event, data, (response: R) => {
          observer.next(response);
          observer.complete();
        });
      };

      if (this.socket.connected) {
        doEmit();
        return;
      }

      // Se não estiver conectado, força conexão e espera o onConnected$ uma vez
      this.datadogService.logWarn('[Socket] emitWithAck called but not connected, forcing connection', {
        event,
        socketId: this.socket.id,
      });
      this.connect();
      firstValueFrom(this.onConnected$)
        .then(() => {
          this.datadogService.logInfo('[Socket] Connection established, emitting event', {
            event,
          });
          doEmit();
        })
        .catch((err) => {
          this.datadogService.logError('[Socket] Failed to connect for emitWithAck', {
            event,
            error: String(err),
          });
          observer.error(err);
        });
    });
  }

  emit<T = any>(event: string, data?: T): void {
    this.socket.emit(event, data);
  }

  /**
   * Envia ping para verificar health da conexão
   * Retorna Observable que completa quando receber pong ou timeout
   */
  ping(): Observable<{ type: string; timestamp: number }> {
    return new Observable((observer) => {
      if (!this.socket.connected) {
        observer.error(new Error('Socket not connected'));
        return;
      }

      this.socket.emit('ping', (response: any) => {
        if (response?.type === 'pong') {
          observer.next(response);
          observer.complete();
        } else {
          observer.error(new Error(response?.message || 'Invalid pong response'));
        }
      });
    });
  }
}
